import {Nullable} from "../../models/common/Nullable";
import {
    errorFetchingRequestState,
    getInitialRequestState,
    RequestState,
    startFetchingRequestState,
    successFetchingRequestState
} from "../../models/common/RequestState";
import {
    CONFIRM_USER_API_KEY_CREATION,
    CREATE_USER_API_KEY,
    CREATE_USER_API_KEY_ERROR,
    CREATE_USER_API_KEY_SUCCESS,
    DELETE_USER_API_KEY,
    DELETE_USER_API_KEY_ERROR,
    DELETE_USER_API_KEY_SUCCESS,
    FETCH_USER_API_KEY,
    FETCH_USER_API_KEY_ERROR,
    FETCH_USER_API_KEY_SUCCESS,
    FETCH_USER_API_KEYS,
    FETCH_USER_API_KEYS_ERROR,
    FETCH_USER_API_KEYS_SUCCESS,
    UserApiKeyActionTypes
} from "../../actions/api-keys/ApiKeysActions";
import {UserApiKey} from "../../models/api-keys/UserApiKey";
import {Identifier, makeStringId} from "../../models/common/Identifier";

/**
 * Represents the detail state for a single user API key entry.
 */
export interface UserApiKeyDetailState {
    /** The fetched API key entity, or null if not yet loaded. */
    userApiKey: Nullable<UserApiKey>
}

/**
 * Represents the full Redux state slice for user API key management.
 *
 * This state tracks the list of API keys, individual detail entries,
 * and the request status for fetching, creating, and deleting operations.
 */
export interface UserApiKeysState {
    /** Total number of API key entries returned by the server. */
    totalEntries: number
    /** The current list of API keys and associated fetch request state. */
    entries: {
        current: Nullable<UserApiKey[]>
        request: RequestState
    }
    /** A map of individually fetched API key details, keyed by their raw identifier. */
    detailedEntries: {
        [key: string]: UserApiKeyDetailState
    }
    createdKey: Identifier< string >
    /** Request state for fetching a single API key detail. */
    detailFetchRequest: RequestState
    /** Request state for create and delete operations. */
    changingRequest: RequestState
}

const initialState: UserApiKeysState = {
    totalEntries: 0,
    entries: {
        current: null,
        request: getInitialRequestState()
    },
    detailedEntries: {},
    createdKey: makeStringId("" ),
    detailFetchRequest: getInitialRequestState(),
    changingRequest: getInitialRequestState()
};

/**
 * Reducer managing the state for user API key operations.
 *
 * Handles fetching the list of keys, fetching individual key details,
 * creating new keys, and deleting existing keys.
 */
export const ApiKeysReducer = (
    state: UserApiKeysState = initialState,
    action: UserApiKeyActionTypes
): UserApiKeysState => {
    switch (action.type) {
        case FETCH_USER_API_KEYS: {
            return {
                ...state,
                entries: {
                    ...state.entries,
                    request: startFetchingRequestState()
                }
            };
        }
        case FETCH_USER_API_KEYS_SUCCESS: {
            return {
                ...state,
                totalEntries: action.payload.totalEntries,
                entries: {
                    current: action.payload.entries,
                    request: successFetchingRequestState()
                }
            };
        }
        case FETCH_USER_API_KEYS_ERROR: {
            return {
                ...state,
                entries: {
                    ...state.entries,
                    request: errorFetchingRequestState(action.error)
                }
            };
        }
        case FETCH_USER_API_KEY:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    [action.apiKeyId.rawValue]: {
                        userApiKey: null
                    }
                },
                detailFetchRequest: startFetchingRequestState()
            };
        case FETCH_USER_API_KEY_SUCCESS: {
            const {rawValue: rawId} = action.userApiKey.apiKeyId;

            if (!state.detailedEntries[rawId]) {
                throw Error("Detail entry undefined! You have to call fetchUserApiKey first!");
            }

            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    [rawId]: {
                        ...state.detailedEntries[rawId],
                        userApiKey: action.userApiKey
                    }
                },
                detailFetchRequest: successFetchingRequestState()
            };
        }
        case FETCH_USER_API_KEY_ERROR: {
            const {rawValue: rawId} = action.apiKeyId;

            if (!state.detailedEntries[rawId]) {
                throw Error("Detail entry undefined! You have to call fetchUserApiKey first!");
            }

            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    [rawId]: {
                        ...state.detailedEntries[rawId]
                    }
                },
                detailFetchRequest: errorFetchingRequestState(action.error)
            };
        }
        case CONFIRM_USER_API_KEY_CREATION:
            return {
                ...state,
                createdKey: makeStringId("" )
            };
        case CREATE_USER_API_KEY:
        case DELETE_USER_API_KEY: {
            return {
                ...state,
                changingRequest: startFetchingRequestState()
            };
        }
        case CREATE_USER_API_KEY_SUCCESS: {
            return {
                ...state,
                createdKey: action.createdKey,
                changingRequest: successFetchingRequestState()
            };
        }
        case DELETE_USER_API_KEY_SUCCESS: {
            return {
                ...state,
                changingRequest: successFetchingRequestState()
            };
        }
        case CREATE_USER_API_KEY_ERROR:
        case DELETE_USER_API_KEY_ERROR: {
            return {
                ...state,
                changingRequest: errorFetchingRequestState(action.error)
            };
        }
        default:
            return state;
    }
};
